import type { ReactNode } from 'react';

/**
 * Dual-render table/card list. Renders an HTML <table> on ≥768px viewports
 * and a stacked card list on <768px. CSS toggles handled via the
 * .resp-table / .resp-cards classes in index.css.
 *
 * See .claude/skills/ui-ux-admin/SKILL.md §2 for the design rationale.
 */

export type ResponsiveListColumn<T> = {
  /** Unique key for the column; also used as the label on mobile cards if label is omitted. */
  key: string;
  /** Header label shown on desktop + as the key in mobile card key:value rows. */
  label: string;
  /** Renderer — return a primitive or ReactNode. No side effects. */
  render: (row: T) => ReactNode;
  /** Right-align the cell on desktop and the value on mobile (useful for numbers). */
  align?: 'left' | 'right';
  /** Promotes this column to the prominent card title on mobile. At most one per table. */
  primaryOnMobile?: boolean;
  /** Hide this column in the mobile card list (keeps it on desktop table). */
  hideOnMobile?: boolean;
  /** Optional minimum width for the column on desktop. */
  minWidth?: number;
};

type Props<T> = {
  rows: T[];
  columns: ResponsiveListColumn<T>[];
  getRowKey: (row: T, index: number) => string;
  /** Optional render fn for an entire mobile card — overrides the default key:value layout. */
  renderMobileCard?: (row: T, index: number) => ReactNode;
  /** Message shown when rows is empty. */
  emptyMessage?: string;
  /** Extra className on the outermost wrapper. */
  className?: string;
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '2px solid var(--color-border)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-sm)',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  padding: 'var(--spacing-3)',
  marginBottom: 'var(--spacing-2)',
};

const cardRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 'var(--spacing-2)',
  paddingTop: 'var(--spacing-1)',
  paddingBottom: 'var(--spacing-1)',
  fontSize: 'var(--font-size-sm)',
};

const cardKeyStyle: React.CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-xs)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  fontWeight: 600,
  flexShrink: 0,
};

const cardValueStyle: React.CSSProperties = {
  color: 'var(--color-text)',
  textAlign: 'right',
  minWidth: 0,
  overflowWrap: 'anywhere',
};

const cardPrimaryStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-md)',
  fontWeight: 600,
  color: 'var(--color-text)',
  marginBottom: 'var(--spacing-2)',
  paddingBottom: 'var(--spacing-2)',
  borderBottom: '1px solid var(--color-border-light)',
  overflowWrap: 'anywhere',
};

export function ResponsiveList<T>({
  rows,
  columns,
  getRowKey,
  renderMobileCard,
  emptyMessage = 'No items.',
  className,
}: Props<T>) {
  if (rows.length === 0) {
    return (
      <p
        style={{
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-sm)',
          padding: 'var(--spacing-3)',
        }}
      >
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className={className}>
      {/* Desktop: HTML table */}
      <div className="resp-table" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{
                    ...thStyle,
                    textAlign: c.align === 'right' ? 'right' : 'left',
                    minWidth: c.minWidth,
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={getRowKey(row, i)}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    style={{
                      ...tdStyle,
                      textAlign: c.align === 'right' ? 'right' : 'left',
                    }}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards */}
      <div className="resp-cards">
        {rows.map((row, i) => {
          if (renderMobileCard) {
            return <div key={getRowKey(row, i)}>{renderMobileCard(row, i)}</div>;
          }
          const primaryCol = columns.find((c) => c.primaryOnMobile);
          const bodyCols = columns.filter((c) => !c.primaryOnMobile && !c.hideOnMobile);
          return (
            <div key={getRowKey(row, i)} style={cardStyle}>
              {primaryCol && <div style={cardPrimaryStyle}>{primaryCol.render(row)}</div>}
              {bodyCols.map((c) => (
                <div key={c.key} style={cardRowStyle}>
                  <span style={cardKeyStyle}>{c.label}</span>
                  <span style={cardValueStyle}>{c.render(row)}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
